import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGuestAuth } from "@/contexts/GuestAuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  Award,
  BedDouble,
  Building2,
  Calendar,
  ConciergeBell,
  CreditCard,
  Hotel,
  LogOut,
  RefreshCw,
  Sparkles,
  UserCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function GuestArea() {
  const { config, profile, isLoading, openAuthModal, logout, refreshProfile } =
    useGuestAuth();
  const navigate = useNavigate();

  useDocumentTitle("Guest Area & Fidelity");

  if (!config?.isEnabled) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center space-y-4">
        <Award className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="text-2xl font-bold">Guest Area non attiva</h1>
        <p className="text-sm text-muted-foreground">
          La Guest Area non è attualmente abilitata per questa struttura.
        </p>
        <Button onClick={() => navigate("/")}>Torna alla Home</Button>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center space-y-5">
        <div className="mx-auto size-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
          <Award className="size-8" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{config.programName}</h1>
          <p className="text-sm text-muted-foreground">
            Accedi al tuo account o registrati per visualizzare la tua tessera
            Fidelity, i punti accumulati, le tue prenotazioni e i servizi
            ordinati.
          </p>
        </div>
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            className="min-h-11 px-5"
            onClick={() => navigate("/guest-area/login")}
          >
            Accedi
          </Button>
          <Button
            className="min-h-11 px-5"
            onClick={() => navigate("/guest-area/register")}
          >
            Registrati ora
          </Button>
        </div>
      </div>
    );
  }

  const cleanName = (profile.name || "").replace(/E2ES\d+$/i, "").trim();
  const cleanSurname = (profile.surname || "").replace(/E2ES\d+$/i, "").trim();
  const fullHolderName = `${cleanName} ${cleanSurname}`.trim() || profile.email;

  const cardGradient = `linear-gradient(135deg, ${
    profile.fidelityColor || "#eab308"
  }dd 0%, #18181b 88%)`;

  return (
    <div className="max-w-4xl mx-auto space-y-6 py-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight">
              Ciao,{" "}
              <strong className="bg-linear-to-tr from-primary to-accent bg-clip-text text-primary/60">
                {fullHolderName}
              </strong>
            </h1>
            <Badge variant="outline" className="text-xs">
              {profile.isOrganizationCentralized ? (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  Gruppo{" "}
                  {profile.organizationName ||
                    profile.registrationStructureName}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <Hotel className="h-3 w-3" />
                  {profile.registrationStructureName}
                </span>
              )}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Iscritto il{" "}
            {new Date(profile.registeredAt).toLocaleDateString("it-IT", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}{" "}
            · {profile.email}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={() => refreshProfile()}
            disabled={isLoading}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Aggiorna
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-destructive/10 cursor-pointer"
            onClick={() => {
              logout();
              navigate("/");
            }}
          >
            <LogOut className="mr-1.5 h-3.5 w-3.5" />
            Esci
          </Button>
        </div>
      </div>

      {/* Fidelity Card + Riepilogo Punti */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
        <div
          className="md:col-span-7 w-full aspect-[1.85/1] max-h-[310px] rounded-2xl px-6 py-5 text-white shadow-xl border border-white/15 flex flex-col justify-between self-start"
          style={{ background: cardGradient }}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest opacity-80">
                {profile.programName}
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold mt-0.5 flex items-center gap-2">
                <Award className="h-6 w-6 sm:h-7 sm:w-7" />
                {profile.fidelityType}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase opacity-80">
                Saldo Punti
              </div>
              <div className="text-2xl sm:text-3xl font-black leading-none mt-0.5">
                {Math.round(profile.currentPoints)}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            {profile.fidelityBenefits && (
              <p className="text-xs opacity-90 truncate">
                {profile.fidelityBenefits}
              </p>
            )}
            {profile.nextFidelityType ? (
              <div className="space-y-1">
                <div className="flex justify-between text-xs opacity-90">
                  <span>
                    Prossimo livello:{" "}
                    <strong>{profile.nextFidelityType}</strong>
                  </span>
                  <span>
                    Mancano{" "}
                    <strong>{Math.round(profile.pointsToNextTier)} pt</strong>
                  </span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-white/20 overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${profile.progressPercentage}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="text-xs font-semibold text-emerald-300 flex items-center gap-1">
                <Sparkles className="h-3.5 w-3.5" />
                Livello massimo raggiunto
              </div>
            )}
          </div>

          <div className="flex items-end justify-between text-xs pt-2.5 border-t border-white/15">
            <div>
              <div className="opacity-70 text-[11px]">Ospite Titolare</div>
              <div className="font-semibold text-sm">{fullHolderName}</div>
            </div>
            <div className="text-right">
              <div className="opacity-70 text-[11px]">Codice Tessera</div>
              <div className="font-mono font-semibold text-sm">
                {profile.cardCode}
              </div>
            </div>
          </div>
        </div>

        {/* Statistiche & Scaglioni */}
        <Card className="md:col-span-5 flex flex-col justify-between">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">
              Il tuo Riepilogo Fedeltà
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2.5 text-center">
              <div className="p-2.5 rounded-xl bg-muted/40 border">
                <div className="text-[11px] text-muted-foreground">
                  Soggiorni
                </div>
                <div className="text-lg font-bold">
                  {profile.reservations.length}
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-muted/40 border">
                <div className="text-[11px] text-muted-foreground">
                  Servizi acquistati
                </div>
                <div className="text-lg font-bold">
                  {profile.orderedServices.length}
                </div>
              </div>
              <div className="col-span-2 p-2.5 rounded-xl bg-muted/40 border">
                <div className="text-[11px] text-muted-foreground">
                  Totale Spese Registrate
                </div>
                <div className="text-lg font-bold text-primary">
                  {profile.totalSpend.toLocaleString("it-IT", {
                    style: "currency",
                    currency: "EUR",
                  })}
                </div>
              </div>
            </div>

            {profile.availableTiers?.length > 0 && (
              <div className="space-y-1.5 pt-2 border-t">
                <div className="text-[11px] font-semibold uppercase text-muted-foreground">
                  Livelli del Piano
                </div>
                <div className="space-y-1">
                  {profile.availableTiers.map((t) => {
                    const isCurrent = t.name === profile.fidelityType;
                    return (
                      <div
                        key={t.id}
                        className={`flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg border ${
                          isCurrent
                            ? "border-primary bg-primary/5 font-semibold"
                            : "border-transparent text-muted-foreground"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className="size-2.5 rounded-full"
                            style={{ backgroundColor: t.color }}
                          />
                          {t.name}
                        </span>
                        <span className="font-mono text-[11px]">
                          {t.maxPoints != null
                            ? `${t.minPoints} – ${t.maxPoints} pt`
                            : `${t.minPoints}+ pt`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Storico Movimenti: Soggiorni, Servizi acquistati, Transazioni */}
      <Tabs defaultValue="reservations" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="reservations" className="cursor-pointer">
            <BedDouble className="mr-1.5 h-4 w-4 hidden sm:inline" />
            Soggiorni ({profile.reservations.length})
          </TabsTrigger>
          <TabsTrigger value="services" className="cursor-pointer">
            <ConciergeBell className="mr-1.5 h-4 w-4 hidden sm:inline" />
            Servizi acquistati ({profile.orderedServices.length})
          </TabsTrigger>
          <TabsTrigger value="transactions" className="cursor-pointer">
            <CreditCard className="mr-1.5 h-4 w-4 hidden sm:inline" />
            Spese ({profile.financialTransactions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reservations" className="space-y-3 pt-4">
          {profile.reservations.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Nessuna prenotazione registrata al momento.
              </CardContent>
            </Card>
          ) : (
            profile.reservations.map((r) => (
              <Card key={r.id}>
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-mono text-xs">
                        #{r.number || r.id}
                      </Badge>
                      <span className="font-semibold text-sm">
                        {r.structureName}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>
                        {r.arrival || "—"} → {r.departure || "—"}
                        {r.nights ? ` (${r.nights} notti)` : ""}
                      </span>
                    </div>
                    {r.roomType && (
                      <div className="text-xs text-muted-foreground">
                        Camera: {r.roomType}{" "}
                        {r.treatment ? `· ${r.treatment}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-base font-bold">
                      {r.amount.toLocaleString("it-IT", {
                        style: "currency",
                        currency: "EUR",
                      })}
                    </div>
                    {r.status && (
                      <Badge variant="outline" className="text-[10px] mt-1">
                        {r.status}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="services" className="space-y-3 pt-4">
          {profile.orderedServices.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  Non hai ancora ordinato servizi aggiuntivi.
                </p>
                <Button size="sm" onClick={() => navigate("/services")}>
                  Scopri i Servizi dell'Hotel
                </Button>
              </CardContent>
            </Card>
          ) : (
            profile.orderedServices.map((os) => (
              <Card key={os.id}>
                <CardContent className="p-4 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">
                        {os.structureName}
                      </span>
                      <Badge
                        variant={os.verified ? "default" : "secondary"}
                        className="text-[10px]"
                      >
                        {os.verified ? "Confermato" : "In lavorazione"}
                      </Badge>
                    </div>
                    <div className="font-bold text-sm">
                      {os.totalPrice.toLocaleString("it-IT", {
                        style: "currency",
                        currency: "EUR",
                      })}
                    </div>
                  </div>
                  <div className="space-y-1 pl-2 border-l-2 border-primary/30">
                    {os.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between text-xs"
                      >
                        <span>
                          {item.quantity}x {item.serviceTitle}
                        </span>
                        <span className="text-muted-foreground">
                          {(item.price * item.quantity).toLocaleString(
                            "it-IT",
                            {
                              style: "currency",
                              currency: "EUR",
                            },
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                  {os.createdAt && (
                    <div className="text-[11px] text-muted-foreground">
                      Ordinato il{" "}
                      {new Date(os.createdAt).toLocaleString("it-IT")}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="transactions" className="space-y-3 pt-4">
          {profile.financialTransactions.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Nessuna transazione contabile registrata.
              </CardContent>
            </Card>
          ) : (
            profile.financialTransactions.map((ft) => (
              <Card key={ft.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">
                      {ft.description || "Addebito Soggiorno"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {ft.structureName} ·{" "}
                      {new Date(ft.transactionDate).toLocaleDateString("it-IT")}
                      {ft.billNumber ? ` · Conto #${ft.billNumber}` : ""}
                    </div>
                  </div>
                  <div className="font-bold text-sm">
                    {ft.amount.toLocaleString("it-IT", {
                      style: "currency",
                      currency: ft.currency || "EUR",
                    })}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
